import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase, type ScheduleItem, type Speaker } from '@/integrations/supabase/client';
import { 
  Card, 
  CardContent,
  CardFooter,
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Trash2, Plus, X, Check, Calendar, Clock } from "lucide-react";

const initialScheduleItemState: Omit<ScheduleItem, 'id'> = {
  day: 'day1',
  start_time: '09:00',
  end_time: '10:00',
  title: '',
  description: '',
  location: '',
  type: 'Session',
  speaker_id: null,
  conference_id: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

const eventTypes = [
  'Keynote',
  'Session',
  'Workshop',
  'Panel',
  'Networking',
  'Break'
];

const ScheduleAdmin = () => {
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Omit<ScheduleItem, 'id'> & { id?: string }>(initialScheduleItemState);
  const [isEditing, setIsEditing] = useState(false);
  const [activeDay, setActiveDay] = useState<string>('day1');
  const { toast } = useToast();

  const fetchScheduleItems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('schedule_items')
        .select(`
          id,
          day,
          start_time,
          end_time,
          title,
          description,
          location,
          type,
          speaker_id
        `)
        .order('start_time');

      if (error) throw error;
      setScheduleItems(data || []);
    } catch (error) {
      console.error('Error fetching schedule items:', error);
      toast({
        title: "Error",
        description: "Failed to load schedule data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSpeakers = async () => {
    try {
      const { data, error } = await supabase
        .from('speakers')
        .select('id, name, title, image_url, topic, company_name, company_logo, twitter_url, linkedin_url, github_url')
        .order('name');

      if (error) throw error;
      
      if (data) {
        const speakersData: Speaker[] = data.map(speaker => ({
          id: speaker.id,
          name: speaker.name,
          title: speaker.title,
          image_url: speaker.image_url,
          topic: speaker.topic,
          company_name: speaker.company_name,
          company_logo: speaker.company_logo,
          twitter_url: speaker.twitter_url,
          linkedin_url: speaker.linkedin_url,
          github_url: speaker.github_url,
          conference_id: null,
          slug: speaker.name.toLowerCase().replace(/\s+/g, '-'),
          bio: '',
          created_at: undefined,
          updated_at: undefined
        }));
        setSpeakers(speakersData);
      }
    } catch (error) {
      console.error('Error fetching speakers:', error);
      toast({
        title: "Error",
        description: "Failed to load speakers data",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchScheduleItems();
    fetchSpeakers();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(initialScheduleItemState);
    setIsEditing(false);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (isEditing && formData.id) {
        const { error } = await supabase
          .from('schedule_items')
          .update({
            day: formData.day,
            start_time: formData.start_time,
            end_time: formData.end_time,
            title: formData.title,
            description: formData.description || null,
            location: formData.location || null,
            type: formData.type || null,
            speaker_id: formData.speaker_id || null
          })
          .eq('id', formData.id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Schedule item updated successfully",
        });
      } else {
        const day1Schedule = [
          {
            day: 'day1',
            start_time: '09:00',
            end_time: '09:20',
            title: 'Keynote I: Creating Impact with AI',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '09:20',
            end_time: '09:40',
            title: 'Keynote II: Decision-Making Challenges: The GenAI Adoption Imperative',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '09:40',
            end_time: '10:00',
            title: 'Keynote III: Build applications faster with AI',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '10:00',
            end_time: '10:20',
            title: 'Keynote IV: Data, Sorted! - How to Turn Mayhem into Insight',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '10:20',
            end_time: '10:50',
            title: 'NETWORKING BREAK (TEAS & SNACKS)',
            type: 'Break',
            location: 'Networking Area'
          },
          {
            day: 'day1',
            start_time: '10:50',
            end_time: '11:10',
            title: 'Keynote V: Inclusion in the AI Economy',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '11:10',
            end_time: '11:30',
            title: 'Keynote VI: AI Governance',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '11:30',
            end_time: '12:30',
            title: 'PANEL SESSION: EXPLORING EMERGING AI MARKET TRENDS',
            type: 'Panel',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '12:30',
            end_time: '13:30',
            title: 'LUNCH BREAK WITH NETWORKING',
            type: 'Break',
            location: 'Dining Area'
          },
          {
            day: 'day1',
            start_time: '13:30',
            end_time: '13:45',
            title: 'Implementing Responsible AI - a case study from the healthcare sector.',
            type: 'Talk',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '13:45',
            end_time: '14:00',
            title: 'Tackling Bias in Large Language Models',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '14:00',
            end_time: '14:15',
            title: 'Sleeping With One AI Open: The (In)Security of AI Supply Chain',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '14:15',
            end_time: '14:30',
            title: 'Accelerating Public Sector AI Adoption Through Tech Enabled Services',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '14:30',
            end_time: '15:00',
            title: 'NETWORKING BREAK (TEAS & SNACKS)',
            type: 'Break',
            location: 'Networking Area'
          },
          {
            day: 'day1',
            start_time: '15:00',
            end_time: '15:15',
            title: "Generative Engine Optimization (Business Reputations in AI chatbots)",
            type: 'Talk',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '15:15',
            end_time: '15:30',
            title: 'AI regulation and Governance',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '15:30',
            end_time: '15:45',
            title: "Bringing the world's open data into a single database - and making it truly searchable",
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day1',
            start_time: '15:45',
            end_time: '16:00',
            title: 'Generative AI in Manufacturing',
            type: 'Session',
            location: 'Main Hall'
          }
        ];

        const day2Schedule = [
          {
            day: 'day2',
            start_time: '09:20',
            end_time: '09:40',
            title: 'Keynote II: The GenAI Industrial Revolution - Democratisation of Expertise using Human/AI Augmentation',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '09:40',
            end_time: '10:00',
            title: 'Keynote III: No good data, no good AI. The power of data ecosystems for AI',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '10:00',
            end_time: '10:20',
            title: 'Keynote IV: From AI Experimentation to Scaled AI Value Creation',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '10:20',
            end_time: '10:50',
            title: 'NETWORKING BREAK (TEAS & SNACKS)',
            type: 'Break',
            location: 'Networking Area'
          },
          {
            day: 'day2',
            start_time: '10:50',
            end_time: '11:10',
            title: 'Exploring Intersection of AI and AR',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '11:10',
            end_time: '11:30',
            title: 'Building a Domain Specific Enterprise AI solutions at Scale',
            type: 'Keynote',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '11:30',
            end_time: '12:30',
            title: 'PANEL SESSION: Emerging Challenges & Opportunities in AI Industry',
            type: 'Panel',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '12:30',
            end_time: '13:30',
            title: 'LUNCH BREAK WITH NETWORKING',
            type: 'Break',
            location: 'Dining Area'
          },
          {
            day: 'day2',
            start_time: '13:30',
            end_time: '13:45',
            title: 'Opportunities in AI FemTech',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '13:45',
            end_time: '14:00',
            title: 'The Metaverse and AI',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '14:00',
            end_time: '14:15',
            title: 'Build Applications with Generative AI and Integrated LLM',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '14:15',
            end_time: '14:30',
            title: 'ROBO-ANALYSTS: LLMs In Market Research',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '14:30',
            end_time: '15:00',
            title: 'NETWORKING BREAK (TEAS & SNACKS)',
            type: 'Break',
            location: 'Networking Area'
          },
          {
            day: 'day2',
            start_time: '15:00',
            end_time: '15:15',
            title: 'Enabling trust of AI, methods for Testing and Validation',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '15:15',
            end_time: '15:30',
            title: 'The Future of Human-AI Interaction',
            type: 'Session',
            location: 'Main Hall'
          },
          {
            day: 'day2',
            start_time: '15:30',
            end_time: '15:45',
            title: 'Trustworthy AI and AI Testing for Industry',
            type: 'Session',
            location: 'Main Hall'
          }
        ];

        const { error: deleteError } = await supabase
          .from('schedule_items')
          .delete()
          .in('day', ['day1', 'day2']);

        if (deleteError) throw deleteError;

        const schedule = [...day1Schedule, ...day2Schedule];

        const { error: insertError } = await supabase
          .from('schedule_items')
          .insert(schedule);

        if (insertError) throw insertError;

        toast({
          title: "Success",
          description: "Schedule updated successfully",
        });
      }
      
      resetForm();
      fetchScheduleItems();
    } catch (error) {
      console.error('Error saving schedule item:', error);
      toast({
        title: "Error",
        description: "Failed to save schedule data",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: ScheduleItem) => {
    setFormData(item);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this schedule item?')) return;
    
    try {
      const { error } = await supabase
        .from('schedule_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Schedule item deleted successfully",
      });
      fetchScheduleItems();
    } catch (error) {
      console.error('Error deleting schedule item:', error);
      toast({
        title: "Error",
        description: "Failed to delete schedule item",
        variant: "destructive",
      });
    }
  };

  const formatTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${minutes} ${ampm}`;
    } catch (e) {
      return time;
    }
  };

  const filteredItems = scheduleItems.filter(item => item.day === activeDay);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Manage Schedule</h2>
        {!showForm && (
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="mr-2 h-4 w-4" /> Add Schedule Item
          </Button>
        )}
      </div>

      {!showForm && (
        <div className="mb-6">
          <Select value={activeDay} onValueChange={setActiveDay}>
            <SelectTrigger className="w-[180px] bg-white/10 text-white border-white/20">
              <SelectValue placeholder="Select Day" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day1">Day 1</SelectItem>
              <SelectItem value="day2">Day 2</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {showForm ? (
        <Card className="mb-8 bg-white/5 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white">
              {isEditing ? 'Edit Schedule Item' : 'Add New Schedule Item'}
            </CardTitle>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">
                    Day *
                  </label>
                  <Select 
                    name="day" 
                    value={formData.day} 
                    onValueChange={(value) => handleSelectChange('day', value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Day" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day1">Day 1</SelectItem>
                      <SelectItem value="day2">Day 2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">
                    Event Type
                  </label>
                  <Select 
                    name="type" 
                    value={formData.type || ''} 
                    onValueChange={(value) => handleSelectChange('type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="start_time" className="text-sm font-medium text-white">
                    Start Time *
                  </label>
                  <Input
                    id="start_time"
                    name="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="end_time" className="text-sm font-medium text-white">
                    End Time *
                  </label>
                  <Input
                    id="end_time"
                    name="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="title" className="text-sm font-medium text-white">
                    Title *
                  </label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Event title"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="location" className="text-sm font-medium text-white">
                    Location
                  </label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location || ''}
                    onChange={handleInputChange}
                    placeholder="Room or location"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">
                    Speaker
                  </label>
                  <Select 
                    name="speaker_id" 
                    value={formData.speaker_id || ''} 
                    onValueChange={(value) => handleSelectChange('speaker_id', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Speaker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None</SelectItem>
                      {speakers.map((speaker) => (
                        <SelectItem key={speaker.id} value={speaker.id}>{speaker.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="description" className="text-sm font-medium text-white">
                    Description
                  </label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    placeholder="Event description"
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button 
                type="button"
                variant="outline" 
                onClick={resetForm}
              >
                <X className="mr-2 h-4 w-4" /> Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Check className="mr-2 h-4 w-4" /> {isEditing ? 'Update' : 'Save'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      ) : null}

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mb-4"></div>
          <p className="text-white">Loading schedule...</p>
        </div>
      ) : filteredItems.length > 0 ? (
        <Card className="bg-white/5 border-purple-500/20 backdrop-blur-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-white/5 border-b border-white/10">
                  <TableHead className="text-white">Time</TableHead>
                  <TableHead className="text-white">Title</TableHead>
                  <TableHead className="text-white">Type</TableHead>
                  <TableHead className="text-white">Location</TableHead>
                  <TableHead className="text-white w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems
                  .sort((a, b) => a.start_time.localeCompare(b.start_time))
                  .map((item) => (
                  <TableRow key={item.id} className="hover:bg-white/5 border-b border-white/10">
                    <TableCell className="font-medium text-white whitespace-nowrap">
                      <Clock className="inline-block h-3 w-3 mr-1 text-purple-400" />
                      {formatTime(item.start_time)} - {formatTime(item.end_time)}
                    </TableCell>
                    <TableCell className="text-gray-300 font-medium">{item.title}</TableCell>
                    <TableCell className="text-gray-300">
                      {item.type && (
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          item.type === 'Keynote' ? 'bg-purple-500/40 text-purple-100' :
                          item.type === 'Session' ? 'bg-blue-500/40 text-blue-100' :
                          item.type === 'Workshop' ? 'bg-green-500/40 text-green-100' :
                          item.type === 'Panel' ? 'bg-yellow-500/40 text-yellow-100' :
                          item.type === 'Networking' ? 'bg-pink-500/40 text-pink-100' :
                          'bg-gray-500/40 text-gray-100'
                        }`}>
                          {item.type}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-300">{item.location}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleEdit(item)}
                          className="text-white hover:text-purple-400 hover:bg-white/10"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(item.id)}
                          className="text-white hover:text-red-400 hover:bg-white/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="text-center py-12 bg-white/5 rounded-lg border border-purple-500/20">
          <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Schedule Items Found</h3>
          <p className="text-gray-400 mb-6">Get started by adding your first schedule item for {activeDay.replace('day', 'Day ')}</p>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="mr-2 h-4 w-4" /> Add Schedule Item
          </Button>
        </div>
      )}
    </div>
  );
};

export default ScheduleAdmin;
